import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";

const OBSERVER_THRESHOLDS = [0.05, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 1];
export function useReaderIntersectionObserver() {
  const { pageOrder } = useParams();
  const [observer, setObserver] = useState<IntersectionObserver | null>(null);
  const lastObservedPage = useRef<number>();
  const observerRef = useRef<HTMLDivElement>(null);
  const isReadyToObserve = useRef(false);

  const navigate = useNavigate();

  // Store pageOrder in a ref to avoid recreating the observer callback
  const pageOrderRef = useRef(pageOrder);
  useEffect(() => {
    pageOrderRef.current = pageOrder;
  }, [pageOrder]);

  const observerCallback: IntersectionObserverCallback = useCallback((entries) => {
    if (!isReadyToObserve.current) {
      return;
    }

    const entry = getProminentEntry(entries);
    if (!entry) {
      return;
    }

    const { intersectionRatio, rootBounds, boundingClientRect, target } = entry;
    if (!target?.id) {
      return;
    }

    if (!rootBounds || !boundingClientRect) {
      return;
    }

    const minThreshold = calcMinThreshold(rootBounds, boundingClientRect);

    // Extract the page index from the element ID (e.g., "vertical-page-2" -> 2)
    const pageIndex = parseInt(target.id.split("-")[2]);
    // pageOrder is 1-based, so convert to 0-based index for comparison
    const currentPageIndex = pageOrderRef.current ? parseInt(pageOrderRef.current) - 1 : undefined;

    if (intersectionRatio >= minThreshold && currentPageIndex !== pageIndex) {
      if (lastObservedPage.current !== pageIndex) {
        const pathArr = window.location.pathname.split("/");
        pathArr[3] = (pageIndex + 1).toString(); // Convert back to 1-based for URL
        const newPath = pathArr.join("/") + window.location.search;
        navigate(newPath, {
          replace: true,
          preventScrollReset: true
        });
        lastObservedPage.current = pageIndex;
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (!observerRef.current) {
      return;
    }

    const newObserver = new IntersectionObserver(observerCallback, {
      root: null,
      threshold: OBSERVER_THRESHOLDS,
    });

    setObserver(newObserver);

    // Enable observation after a brief delay to allow initial render
    const timeoutId = setTimeout(() => {
      isReadyToObserve.current = true;
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      isReadyToObserve.current = false;
      newObserver.disconnect();
      setObserver(null);
    };
  }, [observerCallback]);

  return { observerRef, observer, lastObservedPage };
}

function calcMinThreshold(rootBounds: DOMRect, pageBounds: DOMRect) {
  return Math.min(1, (rootBounds.height / pageBounds.height) * 0.51);
}
function getProminentEntry(entries: IntersectionObserverEntry[]): IntersectionObserverEntry | null {
  // Filter to only intersecting entries first
  const intersectingEntries = entries.filter(e => e.isIntersecting && e.target?.id);

  if (intersectingEntries.length === 0) {
    return null;
  }

  // Find the entry with the highest intersection ratio
  return intersectingEntries.reduce((acc, cur) => {
    return cur.intersectionRatio > acc.intersectionRatio ? cur : acc;
  });
}
